import {
  IntegrationsIcon,
  CheckIcon,
  AttentionTriangleIcon,
} from '@rsos/assets/icons';
import {
  conversationStateValues,
  ConversationTransfer,
  isTCAPIConversationLoaded,
} from '@rsos/reduxDataStore/src/features/conversations/conversations.typings';
import useDisplayPhoneNumber from '../../../../hooks/useDisplayPhoneNumber';
import styles from './TransferBanner.module.scss';

interface TransferBannerProps {
  conversation: conversationStateValues | undefined;
}

/**
 * Renders a banner for a conversation transfer if one is needed, based on the
 * conversation's transfer status.
 */
export function TransferBanner({ conversation }: TransferBannerProps) {
  if (conversation?.transfer?.status === 'pending') {
    return TransferPendingBanner({ transfer: conversation.transfer });
  }
  if (conversation?.transfer?.status === 'timed_out') {
    return TransferTimedOutBanner({ transfer: conversation.transfer });
  }
  if (conversation?.transfer?.status === 'accepted') {
    if (!isTCAPIConversationLoaded(conversation)) {
      // eslint-disable-next-line no-console -- this is needed because callerId is only defined on tcapi calls. We should make it on all call types and this check weon't be needed
      console.error(
        'Conversation transfer status is accepted but conversation data is not from TCAPI',
      );
      return null;
    }
    return (
      <TransferAcceptedBanner
        transfer={conversation.transfer}
        callerId={conversation.data.callerId}
      />
    );
  }
  return null;
}

interface TransferPendingBannerProps {
  transfer: ConversationTransfer;
}

function TransferPendingBanner({ transfer }: TransferPendingBannerProps) {
  const agencyLabel =
    transfer.receivingAuthorityDisplayName ?? 'receiving agency';

  return (
    <div className={styles.pending}>
      <IntegrationsIcon width={14} height={14} />
      <span>Waiting for {agencyLabel} to connect...</span>
    </div>
  );
}

interface TransferAcceptedBannerProps {
  transfer: ConversationTransfer;
  callerId: string | null;
}

function TransferAcceptedBanner({
  transfer,
  callerId,
}: TransferAcceptedBannerProps) {
  const agencyLabel =
    transfer.receivingAuthorityDisplayName ?? 'receiving agency';
  const displayPhoneNumber = useDisplayPhoneNumber(callerId ?? '');

  return (
    <div className={styles.accepted}>
      <CheckIcon width={14} height={14} />

      {displayPhoneNumber ? (
        <span>
          Live chat with{' '}
          <span className={styles.bold}>{displayPhoneNumber}</span> has been
          transferred to <span className={styles.bold}>{agencyLabel}</span>.
        </span>
      ) : (
        <span>Live chat has been transferred to {agencyLabel}.</span>
      )}
    </div>
  );
}

function TransferTimedOutBanner({
  transfer,
}: {
  transfer: ConversationTransfer;
}) {
  const agencyLabel =
    transfer.receivingAuthorityDisplayName ?? 'receiving agency';

  return (
    <div className={styles.timedOut}>
      <AttentionTriangleIcon width={14} height={14} />
      <span>
        Transfer to {agencyLabel} has timed out. You can continue the chat here
        or try transferring again.
      </span>
    </div>
  );
}

export default TransferBanner;
